import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { buildCanonicalUrl } from "lib/seo";

import { objectToQueryString } from "membership-ui/login/utils/utils";
import {
  fetchDatoBriefings,
  BRIEFINGS_PAGE_SIZE,
} from "content-ui/briefings/fetchers/briefingsFetcher";
import { fetchPageData } from "content-ui/briefings/fetchers/briefingsPageFetcher";
import { fetchNavbarAndFooter } from "lib/fetchers/common";
import type {
  BriefingsPageData,
  FetchBriefingsParams,
  BriefingType,
} from "content-ui/briefings/types";

import BriefingsView from "content-ui/briefings";
import PreviewModeBanner from "components/previewModeBanner/previewModeBanner";
import ErrorBoundary from "components/errorBoundary/errorBoundary";
import Navigation from "components/navigation/navigation";
import Footer from "components/footer/footer";

export async function getServerSideProps({ res, preview = false }) {
  const params: FetchBriefingsParams = {
    preview,
    skip: 0,
    first: BRIEFINGS_PAGE_SIZE,
    orderBy: "lastUpdated_DESC",
    filter: [],
  };
  const [menuItems, briefings, page] = await Promise.all([
    fetchNavbarAndFooter(),
    fetchDatoBriefings(params),
    fetchPageData({ preview }),
  ]);

  res.setHeader(
    "Cache-Control",
    "public, s-maxage=10, stale-while-revalidate=59"
  );

  return {
    props: {
      briefings: briefings,
      previewMode: preview,
      params: params,
      pageData: page,
      __initialState: {
        sitewideOption: { ...menuItems },
      },
    },
  };
}

export type BriefingsPageProps = {
  previewMode: boolean;
  pageData: BriefingsPageData;
  params: FetchBriefingsParams;
  briefings: {
    list: BriefingType[];
    count: number;
  };
};

const BriefingsPage = (props: BriefingsPageProps) => {
  const { pageData } = props;

  const seoImage = `${pageData.seo.image.url}?${objectToQueryString({
    w: "1200",
    h: "628",
    q: "75",
    fit: "crop",
    auto: "format,compress",
  })}`;

  return (
    <>
      <Head>
        {generateNextSeo({
          canonical: buildCanonicalUrl("/pro/briefings"),
          openGraph: {
            title: pageData.seo.title,
            description: pageData.seo.description,
            images: [{ url: seoImage }],
          },
          twitter: {
            handle: "@siftedeu",
            site: "@siftedeu",
            cardType: "summary_large_image",
          },
        })}
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <Navigation />
      {props.previewMode ? (
        <PreviewModeBanner exitSlug={`/pro/briefings`} />
      ) : null}
      <ErrorBoundary>
        <BriefingsView {...props} />
      </ErrorBoundary>
      <Footer />
    </>
  );
};

export default BriefingsPage;
